import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { PICKER_MENU_SELECTOR } from './PickerMenuShell';

const MENU_FOCUSABLE_SELECTOR = 'button:not(:disabled), textarea';

// True when Tab would carry focus out of the portaled menu, past its first or last control.
function isTabOutOfMenu(e: KeyboardEvent): boolean {
    if (e.key !== 'Tab' || !(e.target instanceof HTMLElement)) return false;
    const focusables = e.target
        .closest(PICKER_MENU_SELECTOR)
        ?.querySelectorAll<HTMLElement>(MENU_FOCUSABLE_SELECTOR);
    if (!focusables?.length) return false;
    const edge = e.shiftKey ? focusables[0] : focusables[focusables.length - 1];
    return e.target === edge;
}

// Open state for a trigger + menu pair; the FloatingMenu anchored to root owns outside clicks.
export function usePickerMenu() {
    const rootRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((prev) => !prev), []);
    useEscapeKey(close, open);
    // The root's only DOM button is the trigger; the menu is portaled elsewhere.
    const closeToTrigger = useCallback(() => {
        close();
        rootRef.current?.querySelector('button')?.focus();
    }, [close]);
    // Table cells stop keydown from bubbling, so the document-level Escape never fires there.
    // Menu keydowns bubble here through the portal.
    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key !== 'Escape' && !isTabOutOfMenu(e)) return;
            e.preventDefault();
            closeToTrigger();
        },
        [closeToTrigger]
    );
    return { rootRef, open, close, closeToTrigger, toggle, onKeyDown };
}
